import {
  type CheckboxProps,
  FormControlLabel,
  type FormControlLabelProps,
  type SxProps,
  type Theme,
  useTheme
} from '@mui/material'
import { MolsonBox } from '../molson-box'
import { MolsonButton } from '../molson-button'
import { MolsonDefaultCheckbox } from '../molson-default-checkbox'
import { MolsonTextField } from '../molson-textfield'
import { MolsonTypography } from '../molson-typography'

export interface MolsonCheckboxProps extends CheckboxProps {
  formControlLabelProps?: Omit<FormControlLabelProps, 'control'>
  dataTestId?: string
  checked?: boolean
  label?: string | React.ReactNode
  secondLabel?: string | React.ReactNode
  secondLabelSx?: SxProps<Theme>
  outerBoxSx?: SxProps<Theme>
  withQuantityUpdate?: boolean
  handleDecrease?: () => void
  handleIncrease?: () => void
  quantityValue?: number
}

export const secondLabelDefaultSx = (theme: Theme) => ({
  color: theme.palette.secondary[600],
  fontSize: '0.875rem',
  lineHeight: '24px',
  fontWeight: theme.typography.fontWeightMedium,
  ml: 4.125
})

export const MolsonCheckbox = ({
  dataTestId,
  checked = true,
  label,
  secondLabel,
  outerBoxSx,
  secondLabelSx,
  withQuantityUpdate,
  handleDecrease,
  handleIncrease,
  quantityValue,
  formControlLabelProps,
  ...props
}: MolsonCheckboxProps & Pick<FormControlLabelProps, 'labelPlacement'>) => {
  const theme = useTheme()
  const { disabled } = props

  const borderColor = () => {
    const border = '1.5px solid '
    if (checked) {
      return border + theme.palette.primary.dark
    }
    return `${border}transparent`
  }

  const backgroundColor = () => {
    if (checked) {
      return theme.palette.secondary[200]
    }
    return 'transparent'
  }

  return (
    <MolsonBox
      sx={{
        py: 2.25,
        px: 2,
        borderRadius: 2,
        border: borderColor(),
        backgroundColor: backgroundColor(),
        display: 'flex',
        flexDirection: 'row',
        alignContent: 'center',
        justifyContent: 'space-between',
        '&:hover': {
          cursor: disabled ? 'not-allowed' : 'auto'
        },
        ...outerBoxSx
      }}
    >
      <MolsonBox display="flex" flexDirection="column">
        <FormControlLabel
          control={
            <MolsonDefaultCheckbox
              checked={checked}
              data-testid={dataTestId}
              {...props}
            />
          }
          {...formControlLabelProps}
          label={label}
        />
        {secondLabel && (
          <MolsonTypography
            sx={{ ...secondLabelDefaultSx(theme), ...secondLabelSx }}
          >
            {secondLabel}
          </MolsonTypography>
        )}
      </MolsonBox>
      {withQuantityUpdate ? (
        <MolsonBox display="flex" gap={1} alignItems="center">
          <MolsonButton
            color="secondary"
            variant="outlined"
            onClick={handleDecrease}
            disabled={quantityValue! <= 1}
            sx={{ bgcolor: 'transparent' }}
          >
            -
          </MolsonButton>
          <MolsonTextField
            InputProps={{
              style: {
                height: 42,
                width: 42
              }
            }}
            inputProps={{
              style: {
                padding: 0,
                textAlign: 'center'
              }
            }}
            disabled={disabled}
            value={quantityValue}
          />
          <MolsonButton
            color="secondary"
            variant="outlined"
            onClick={handleIncrease}
            sx={{ bgcolor: 'transparent' }}
          >
            +
          </MolsonButton>
        </MolsonBox>
      ) : null}
    </MolsonBox>
  )
}
